import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import './App.css';
import { canAccessPage } from './config/navigation';
import frontendPackage from '../package.json';

// Components
import Login from './components/Login';
import ChangePassword from './components/ChangePassword';
import Profile from './components/Profile';
import Dashboard from './components/Dashboard';
import Funnels from './components/Funnels';
import Leads from './components/Leads';
import Pipeline from './components/Pipeline';
import Projects from './components/Projects';
import BulkUpload from './components/BulkUpload';
import Tasks from './components/Tasks';
import Users from './components/Users';
import Profiles from './components/Profiles';
import ChatBot from './components/ChatBot';
import EmailMarketing from './components/EmailMarketing';
import UniqueChannel from './components/UniqueChannel';
import NexoHub from './components/NexoHub';
import NexoBid from './components/NexoBid';
import Tutorial from './components/Tutorial';
import LeadCapture from './components/LeadCapture';
import Deals from './components/Deals';
import Activities from './components/Activities';
import Calendar from './components/Calendar';
import Integrations from './components/Integrations';
import AdvancedCreation from './components/AdvancedCreation';
import EmailFinder from './components/prospecting/EmailFinder';
import LinkedInCampaigns from './components/prospecting/LinkedInCampaigns';
import LeadExtraction from './components/LeadExtraction';
import Settings from './components/settings/Settings';
import Proposals from './components/Proposals';
import Companies from './components/Companies';
import PipelineIntelligence from './components/PipelineIntelligence';
import NexoIntelligence from './components/NexoIntelligence';
import Quotes from './components/Quotes';
import SocialIntegrations from './components/SocialIntegrations';
import MetaCampaigns from './components/MetaCampaigns';
import TaskReminders from './components/TaskReminders';
import Billing from './components/Billing';
import CallCopilot from './components/CallCopilot';
import CallCopilotPlaybooks from './components/CallCopilotPlaybooks';
import CallCopilotResponses from './components/CallCopilotResponses';
import CallFeedback from './components/CallFeedback';
import CallAnalytics from './components/CallAnalytics';
import Sidebar from './components/Sidebar';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NEXOSYNX_SYSTEM_VERSION = process.env.REACT_APP_VERSION || frontendPackage.version || '0.1.0';
const NEXOSYNX_FOOTER_COPY = `© ${new Date().getFullYear()} NEUROSYNX LTDA • NexoSynX CRM • Versão ${NEXOSYNX_SYSTEM_VERSION}`;

function NexoSynXFooter({ hasSidebar = false }) {
  return (
    <footer
      className={`mt-auto border-t border-slate-200 bg-white px-4 py-3 ${
        hasSidebar ? 'md:ml-64' : ''
      }`}
    >
      <div className="flex items-center justify-center">
        <p className="text-center text-[11px] font-medium tracking-wide text-slate-500">
          {NEXOSYNX_FOOTER_COPY}
        </p>
      </div>
    </footer>
  );
}

// Create axios instance with auth. `onUnauthorized`, quando informado, é
// chamado sempre que o backend responder 401 (token expirado/inválido) —
// sem isso, chamadas feitas depois do login expirar falhavam silenciosamente
// e o usuário ficava travado nas telas sem entender o motivo.
// `onPlanExpired` é chamado quando o backend responde 402 (plano da conta
// vencido — trava aplicada em get_current_user/login no backend).
// `onIpBlocked` é chamado quando o backend recusa a requisição porque a rede atual
// não está autorizada (Configurações > Bloqueio por IP).
const IP_BLOCK_MESSAGE = 'Acesso não permitido a partir desta rede';
const createAxiosInstance = (token, onUnauthorized, onPlanExpired, onIpBlocked) => {
  const instance = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (onUnauthorized || onPlanExpired || onIpBlocked) {
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 401 && onUnauthorized) {
          onUnauthorized();
        } else if (status === 402 && onPlanExpired) {
          onPlanExpired();
        } else if (
          status === 403 && onIpBlocked &&
          !String(error?.config?.url || '').includes('/auth/login') &&
          String(error?.response?.data?.detail || '').includes(IP_BLOCK_MESSAGE)
        ) {
          onIpBlocked();
        }
        return Promise.reject(error);
      }
    );
  }
  return instance;
};

const canUserAccessPage = (page, user) => {
  if (page === 'billing') return true;
  if (page === 'tutorial') return true;
  if (page === 'profile') return true;

  // Mantém compatibilidade com as páginas adicionadas pela integração Meta
  // até que elas também estejam cadastradas em config/navigation.
  if (page === 'meta-campaigns' || page === 'tiktok-campaigns') {
    return user?.permissions?.includes('view_integrations');
  }

  return canAccessPage(page, user);
};

function App() {
  const requestedPage = new URLSearchParams(window.location.search).get('page');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(requestedPage || 'dashboard');
  const [chatMinimized, setChatMinimized] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
  });
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebarCollapsed', next ? '1' : '0'); } catch { /* sem storage */ }
      return next;
    });
  };
  const [coachMessage, setCoachMessage] = useState(null);
  const [planExpired, setPlanExpired] = useState(false);
  const lastCoachCheckRef = React.useRef(0);
  const sessionExpiredRef = React.useRef(false);
  const ipBlockedRef = React.useRef(false);

  const handleLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentPage('dashboard');
    setPlanExpired(false);
  }, []);

  // Chamado por qualquer instância da API quando o backend responde 401
  // (token expirado/inválido). Sem isso o usuário ficava preso na tela
  // (ex.: editor de tarefa) vendo erros genéricos sem conseguir salvar nada.
  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    handleLogout();
    toast.error('Sua sessão expirou. Faça login novamente.');
    setTimeout(() => { sessionExpiredRef.current = false; }, 3000);
  }, [handleLogout]);

  // Chamado quando o backend responde 403 de "rede não autorizada" (bloqueio por IP ativado
  // com a sessão já aberta). Várias requisições falham juntas: avisa uma única vez, espera
  // 8 segundos para a pessoa salvar o que estiver fazendo e só então desconecta.
  const handleIpBlocked = useCallback(() => {
    if (ipBlockedRef.current) return;
    ipBlockedRef.current = true;
    toast.error('A rede atual não está autorizada a acessar o sistema.', {
      description: 'Você será desconectado em 8 segundos. Salve o que estiver fazendo.',
      duration: 8000,
      style: { background: '#173efc', color: '#fff', fontWeight: 700 },
    });
    setTimeout(() => {
      ipBlockedRef.current = false;
      handleLogout();
    }, 8000);
  }, [handleLogout]);

  // Chamado quando o backend responde 402 (plano da conta vencido). Mantém o
  // usuário "logado" (não limpa o token) — assim que o plano for renovado, um
  // reload já libera o acesso.
  const handlePlanExpired = useCallback(() => {
    setCurrentPage('billing');
    setPlanExpired(true);
  }, []);

  // Keep the API client identity stable. Components such as Calendar use it
  // in effect dependencies; recreating it on every App render caused the same
  // request to be fired repeatedly.
  const api = useMemo(
    () => createAxiosInstance(token, handleSessionExpired, handlePlanExpired, handleIpBlocked),
    [token, handleSessionExpired, handlePlanExpired, handleIpBlocked]
  );

  // Telas que usam fetch direto (ex.: Config. E-mail) não passam pelo interceptador do axios:
  // observa as respostas do fetch para a nossa API e trata o mesmo 403 de "rede não autorizada".
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const input = args[0];
        const url = typeof input === 'string' ? input : input?.url || '';
        if (response.status === 403 && url.startsWith(API) && !url.includes('/auth/login')) {
          const body = await response.clone().json().catch(() => null);
          if (String(body?.detail || '').includes(IP_BLOCK_MESSAGE)) handleIpBlocked();
        }
      } catch { /* nunca atrapalha a resposta original */ }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, [handleIpBlocked]);

  useEffect(() => {
    const handler = (e) => setCurrentPage(e.detail);
    window.addEventListener('nexosynx:navigate', handler);
    return () => window.removeEventListener('nexosynx:navigate', handler);
  }, []);

  // Redireciona para o Dashboard quando o usuário acessa uma página sem permissão
  useEffect(() => {
    if (!user) return;
    if (!canUserAccessPage(currentPage, user) && currentPage !== 'dashboard') {
      setCurrentPage('dashboard');
    }
  }, [currentPage, user]);

  // Maya Coach Engine — verifica negócios em risco a cada 5 min
  useEffect(() => {
    if (!token || !user) return;
    const runCoach = async () => {
      const now = Date.now();
      if (now - lastCoachCheckRef.current < 5 * 60 * 1000) return;
      lastCoachCheckRef.current = now;
      try {
        const coachApi = createAxiosInstance(token, handleSessionExpired, handlePlanExpired, handleIpBlocked);
        const r = await coachApi.get('/pipeline/rotting-deals?days_threshold=14');
        const deals = r.data?.rotting_deals || [];
        if (deals.length > 0) {
          const top = deals[0];
          setCoachMessage(
            `🎯 Coach de Vendas: Detectei ${deals.length} negócio(s) parado(s)! O negócio "${top.title}" (R$ ${(top.value || 0).toLocaleString('pt-BR')}) está sem atividade há ${top.days_without_activity || 14}+ dias. Deseja que eu sugira uma ação de reativação?`
          );
          setChatMinimized(false);
        }
      } catch {}
    };
    // Roda após 30s do login e depois a cada 5 min
    const timer = setTimeout(runCoach, 30000);
    const interval = setInterval(runCoach, 5 * 60 * 1000);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, [token, user]);

  useEffect(() => {
    if (token) {
      // Se o usuário precisa trocar a senha, não valida via rotas protegidas
      // (elas retornam 403 até a troca) — vai direto para a tela de troca.
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (storedUser?.must_change_password) {
        setUser(storedUser);
        setLoading(false);
        return;
      }
      // Validate token and get user data
      const api = createAxiosInstance(token);
      api.get('/dashboard/stats')
        .then(() => {
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          setUser(userData);
        })
        .catch((error) => {
          if (error?.response?.status === 402) {
            // Plano vencido: mantém o token/usuário e libera apenas faturamento.
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            setUser(userData);
            setCurrentPage('billing');
            setPlanExpired(true);
            return;
          }
          if (
            error?.response?.status === 403 &&
            String(error?.response?.data?.detail || '').includes(IP_BLOCK_MESSAGE)
          ) {
            // Recarregou a página numa rede não autorizada: sem sessão a apagar em 8 s,
            // vai direto ao login explicando o motivo.
            toast.error(error.response.data.detail, {
              duration: 10000,
              style: { background: '#173efc', color: '#fff', fontWeight: 700 },
            });
          }
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handlePasswordChanged = () => {
    const updatedUser = { ...user, must_change_password: false };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const renderCurrentPage = () => {
    if (planExpired) return <Billing api={api} />;

    // Tutorial é público para todos os usuários
    if (currentPage === 'tutorial') return <Tutorial />;

    // Perfil é de todo usuário logado (nome, e-mail, perfil e troca de senha)
    if (currentPage === 'profile') return <Profile api={api} user={user} />;

    // Gating central por tela (admin sempre; modelo por tela ou legado).
    if (!canUserAccessPage(currentPage, user)) return <AccessDenied />;

    switch (currentPage) {
      case 'dashboard':
        return <Dashboard api={api} onNavigate={setCurrentPage} />;
      case 'funnels':
        return <Funnels api={api} />;
      case 'leads':
        return <Leads api={api} />;
      case 'deals':
        return <Deals api={api} user={user} setCurrentPage={setCurrentPage} />;
      case 'activities':
        return <Activities api={api} user={user} />;
      case 'agenda':
        return <Calendar api={api} user={user} />;
      case 'integrations':
        return <Integrations api={api} />;
      case 'creation':
        return <AdvancedCreation api={api} />;
      case 'email-finder':
        return <EmailFinder api={api} onOpenIntegrations={() => setCurrentPage('integrations')} />;
      case 'linkedin-campaigns':
        return <LinkedInCampaigns api={api} />;
      case 'lead-extraction':
        return <LeadExtraction api={api} />;
      case 'settings':
        return <Settings api={api} user={user} />;
      case 'api-configurations':
      case 'email-config':
        return <Settings api={api} user={user} />;
      case 'companies':
        return <Companies api={api} />;
      case 'proposals':
        return <Proposals api={api} />;
      case 'pipeline-intelligence':
        return <PipelineIntelligence api={api} />;
      case 'quotes':
        return <Quotes api={api} />;
      case 'social-integrations':
        return <SocialIntegrations api={api} />;
      case 'meta-campaigns':
        return <MetaCampaigns api={api} />;
      case 'tiktok-campaigns':
        return <MetaCampaigns api={api} platform="tiktok" />;
      case 'pipeline':
        return <Pipeline api={api} />;
      case 'projects':
        return <Projects api={api} user={user} />;
      case 'bulk-upload':
        return <BulkUpload api={api} />;
      case 'tasks':
        return <Tasks api={api} user={user} />;
      case 'email-marketing':
        return <EmailMarketing api={api} />;
      case 'unique-channel':
        return <UniqueChannel api={api} />;
      case 'nexo-intelligence':
        return <NexoIntelligence api={api} />;
      case 'nexohub':
        return <NexoHub api={api} />;
      case 'nexobid':
        return <NexoBid api={api} />;
      case 'call-copilot':
        return <CallCopilot api={api} onNavigate={setCurrentPage} />;
      case 'call-playbooks':
        return <CallCopilotPlaybooks api={api} />;
      case 'call-responses':
        return <CallCopilotResponses api={api} />;
      case 'call-feedback':
        return <CallFeedback api={api} />;
      case 'call-analytics':
        return <CallAnalytics api={api} />;
      case 'users':
        return <Users api={api} />;
      case 'profiles':
        return <Profiles api={api} />;
      case 'billing':
        return <Billing api={api} />;
      default:
        return <Dashboard api={api} />;
    }
  };

  const AccessDenied = () => (
    <div className="flex items-center justify-center h-96">
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Acesso Negado</h3>
        <p className="text-slate-600">Você não tem permissão para acessar esta funcionalidade.</p>
        <p className="text-sm text-slate-500 mt-2">Entre em contato com o administrador do sistema.</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {!user ? (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
          <Routes>
            <Route path="/capture" element={<LeadCapture />} />
            <Route path="*" element={<Login onLogin={handleLogin} />} />
          </Routes>
        </div>
      ) : user.must_change_password ? (
        <ChangePassword
          token={token}
          user={user}
          onDone={handlePasswordChanged}
          onLogout={handleLogout}
        />
      ) : (
        <div className="min-h-screen bg-gray-50 flex">
          <Sidebar
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            user={user}
            onLogout={handleLogout}
            mobileOpen={mobileSidebarOpen}
            onMobileClose={() => setMobileSidebarOpen(false)}
            billingOnly={planExpired}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapsed}
          />

          {/* Topbar mobile/tablet: some com o Sidebar fixo em telas lg+ */}
          <header
            className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 h-14 shadow-sm"
            style={{ background: '#0B1220', borderBottom: '1px solid rgba(37,99,235,0.12)' }}
          >
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="p-2 -ml-2 rounded-lg text-white"
              aria-label="Abrir menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <svg width="26" height="26" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <defs>
                <linearGradient id="topbargrad" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#2563EB" />
                  <stop offset="100%" stopColor="#00D4CB" />
                </linearGradient>
              </defs>
              <path d="M10 44 L10 14 L18 14 L36 36 L36 14 L44 14 L44 44 L36 44 L18 22 L18 44 Z" fill="url(#topbargrad)" />
              <circle cx="36" cy="10" r="5" fill="#00D4CB" />
            </svg>
            <span className="text-white font-bold text-base">NexoSynX</span>
          </header>

          <main className={`flex-1 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} min-w-0 pt-14 lg:pt-0 transition-[margin] duration-300`}>
            <div className="p-4 sm:p-6 lg:p-8 min-w-0">
              {renderCurrentPage()}
            </div>
          </main>
          
          {/* Task Reminders */}
          {!planExpired && <TaskReminders api={api} user={user} />}
          
          {/* ChatBot */}
          {!planExpired && (
            <ChatBot
              api={api}
              isMinimized={chatMinimized}
              onToggle={(action) => {
                if (action === 'open') setChatMinimized(false);
                else setChatMinimized(prev => !prev);
              }}
              proactiveMessage={coachMessage}
            />
          )}
        </div>
      )}
      {user && <NexoSynXFooter hasSidebar={!user.must_change_password} />}
      <Toaster />
    </BrowserRouter>
  );


}

export default App;
